import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/constants";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = signed out, object = signed in
  const [authOpen, setAuthOpen] = useState(false);
  const bootstrapped = useRef(false);

  const openAuth = useCallback(() => setAuthOpen(true), []);
  const closeAuth = useCallback(() => setAuthOpen(false), []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      // Returning from Google OAuth — exchange session_id before anything else.
      const sessionId = hash.split("session_id=")[1].split("&")[0];
      axios
        .post(`${API}/auth/google/session`, { session_id: sessionId }, { withCredentials: true })
        .then(({ data }) => {
          setUser(data);
          toast.success(`Welcome to the pod, ${(data.name || data.email).split(" ")[0]}!`);
        })
        .catch(() => {
          setUser(false);
          toast.error("Google sign-in didn't surface — please try again.");
        })
        .finally(() =>
          window.history.replaceState({}, "", window.location.pathname + window.location.search)
        );
    } else {
      refresh();
    }
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(false);
    toast.success("See you on the next wave!");
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, authOpen, openAuth, closeAuth, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;
